import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useBrand } from './BrandContext';

const AuthContext = createContext(null);

export function AuthProvider({ children, onMeLoaded }) {
  const [user, setUser] = useState(null);
  // Roles and permissions are granted per brand; /api/me returns them for every brand the user can
  // open, and useAuth picks the active brand's.
  const [permissionsByBrand, setPermissionsByBrand] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(() => {
    setLoading(true);
    return api.get('/api/me')
      .then(res => {
        setUser({ ...res.data.user, avatar: res.data.avatar ?? null });
        setPermissionsByBrand(res.data.permissions_by_brand || {});
        onMeLoaded?.(res.data);
      })
      .catch(() => {
        setUser(null);
        setPermissionsByBrand({});
      })
      .finally(() => setLoading(false));
  }, [onMeLoaded]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = () => {
    window.location.href = '/auth/logout';
  };

  return (
    <AuthContext.Provider value={{ user, permissionsByBrand, loading, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

// Must be used inside BrandProvider: permissions are those on the active brand.
export function useAuth() {
  const auth = useContext(AuthContext);
  const brand = useBrand()?.activeBrandInfo?.key;
  const permissions = (brand && auth.permissionsByBrand[brand]) || NO_PERMISSIONS;
  const hasPermission = (perm) => permissions.includes(perm);
  return { ...auth, permissions, hasPermission };
}

const NO_PERMISSIONS = [];
